import { EFinancingCompany, EFinancingProgram, EProvince } from '@if/core/types'
import { FinancingProgramProvider } from '@src/data/types/FinancingProgramProvider'
import { MechanicalWarrantyProduct } from '@src/data/types/MechanicalWarrantyProduct'
import { PromoPlan } from '@src/data/types/PromoPlan'
import { getApiClient } from '@src/services/api-client'
import { skipToken, useQueries, useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { Merchant } from '../../types'
import { SearchMerchantDto } from './SearchMerchantDto'

const SCOPE_MERCHANTS = 'merchants'
const LIST = 'list'
const DETAIL = 'detail'
const PROVIDERS = 'providers'
const PRODUCTS = 'products'
const APPLICABLE_PROMO_PLANS = 'applicablePromoPlans'
const RESOLVED_PLAN = 'resolvedPlan'

const MERCHANTS_PER_PROGRAM_LIMIT = 25

const keysFactory = {
  allMerchants: [SCOPE_MERCHANTS] as const,
  allMerchantsLists: [SCOPE_MERCHANTS, LIST] as const,
  merchantsList: (searchDto: SearchMerchantDto) => [SCOPE_MERCHANTS, LIST, searchDto] as const,
  allMerchantsDetails: [SCOPE_MERCHANTS, DETAIL] as const,
  merchantDetail: (id?: string) => [SCOPE_MERCHANTS, DETAIL, id ?? ''] as const,
  allMerchantProviders: [SCOPE_MERCHANTS, PROVIDERS] as const,
  merchantProviders: (merchantId: string, financingProgramId: number) =>
    [SCOPE_MERCHANTS, PROVIDERS, merchantId, financingProgramId] as const,
  allMerchantProviderProducts: [SCOPE_MERCHANTS, PRODUCTS] as const,
  merchantProviderProducts: (merchantId: string, financingProgramId: number, providerId: string) =>
    [SCOPE_MERCHANTS, PRODUCTS, merchantId, financingProgramId, providerId] as const,
  applicablePromoPlans: (merchantId?: string) => [SCOPE_MERCHANTS, APPLICABLE_PROMO_PLANS, merchantId ?? ''] as const,
  resolvedPlan: (merchantId: string, paymentPlanId: string, financingProgramId: string) => 
    [SCOPE_MERCHANTS, RESOLVED_PLAN, merchantId, paymentPlanId, financingProgramId] as const,
}

const transMerchantFromApi = (data: Merchant): Merchant => ({
  ...data,
  province: (data.province?.toUpperCase() ?? '') as EProvince,
})

// Query functions
const getMerchants = async (searchDto: SearchMerchantDto) => {
  const response = await getApiClient().post<Merchant[]>('/Merchants/search', searchDto)
  return response.data.map(transMerchantFromApi)
}

const getApplicablePromoPlans = async (merchantId: string) => {
  const response = await getApiClient().get<PromoPlan[]>(`/Merchants/${merchantId}/ApplicablePromoPlans`)
  return response.data
}

const resolveApplicablePlans = async (merchantId: string, paymentPlanId: string | null, financingProgramId: string) => {
  const response = await getApiClient().get<PromoPlan[]>(
    `/Merchants/${merchantId}/ApplicablePlans`,
    { params: { paymentPlanId, financingProgramId } },
  )
  return response.data[0] ?? null
}

export function useResolveApplicablePlans(
  merchantId: string | undefined,
  paymentPlanId: string | null,
  financingProgramId: string,
): [PromoPlan | null, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.resolvedPlan(merchantId ?? '', paymentPlanId ?? '', financingProgramId),
    queryFn: merchantId ? () => resolveApplicablePlans(merchantId, paymentPlanId, financingProgramId) : skipToken,
  })
  return [data ?? null, !!merchantId && isFetching]
}

export function useGetApplicablePromoPlans(merchantId?: string): [PromoPlan[], boolean, AxiosError | null] {
  const { data, isFetching, error } = useQuery<PromoPlan[], AxiosError>({
    queryKey: keysFactory.applicablePromoPlans(merchantId),
    queryFn: merchantId ? () => getApplicablePromoPlans(merchantId) : skipToken,
  })
  return [data ?? [], !!merchantId && isFetching, error]
}

// Object-shaped variant matching ResourceAutocompleteSearchResult from @if/mui.
export function useSearchMerchantsForResource(searchDto: SearchMerchantDto | null) {
  const isEnabled = !!searchDto && !!searchDto.nameContains
  const { data, isLoading, isFetching } = useQuery({
    queryKey: keysFactory.merchantsList(searchDto!),
    queryFn: isEnabled ? () => getMerchants(searchDto) : skipToken,
  })
  return { data, isLoading, isFetching }
}

export function useSearchMerchants(searchDto: SearchMerchantDto | null): [Merchant[], boolean] {
  const { data, isFetching } = useSearchMerchantsForResource(searchDto)
  return [data ?? [], isFetching]
}

/**
 * Every merchant on a financing program, with no name filter — for programs served by so few
 * merchants that the caller can pick without searching. Pass a null program to skip the query.
 */
export function useMerchantsForProgram(
  financingProgramId: EFinancingProgram | null,
  financingCompanyId: EFinancingCompany | null,
): [Merchant[], boolean] {
  const searchDto: SearchMerchantDto | null = financingProgramId
    ? { nameContains: '', financingProgramId, financingCompanyId, limit: MERCHANTS_PER_PROGRAM_LIMIT, isActive: true }
    : null
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.merchantsList(searchDto!),
    queryFn: searchDto ? () => getMerchants(searchDto) : skipToken,
  })
  return [data ?? [], !!searchDto && isFetching]
}

const getMerchantById = async (id: string) => {
  const response = await getApiClient().get<Merchant>(`/Merchants/${id}`)
  return transMerchantFromApi(response.data)
}

// Object-shaped variant matching ResourceAutocompleteFindResult from @if/mui.
export function useFindMerchantById(id: string | null | undefined) {
  const { data, isLoading, isFetching } = useQuery({
    queryKey: keysFactory.merchantDetail(id ?? ''),
    queryFn: id ? () => getMerchantById(id) : skipToken,
  })
  return { data, isLoading: !!id && isLoading, isFetching: !!id && isFetching }
}

export function useGetMerchantById(id?: string): [Merchant | null, boolean] {
  const { data, isFetching } = useFindMerchantById(id)
  return [data ?? null, isFetching]
}

export function useGetMerchantsByIds(ids: string[]): [Merchant[], boolean] {
  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: keysFactory.merchantDetail(id),
      queryFn: () => getMerchantById(id),
      enabled: !!id,
    })),
  })

  const merchants = results.map((result) => result.data).filter((m): m is Merchant => m !== undefined)
  const isFetching = results.some((result) => result.isFetching)

  return [merchants, isFetching]
}

const getMerchantProviderByType = async (merchantId: string, financingProgramId: number) => {
  const response = await getApiClient().get<FinancingProgramProvider[]>(
    `/Merchants/${merchantId}/Providers/${financingProgramId}`,
  )
  return response.data
}

export function useGetMerchantProviderByType(
  merchantId: string,
  financingProgramId: number,
): [FinancingProgramProvider[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.merchantProviders(merchantId, financingProgramId),
    queryFn: () => getMerchantProviderByType(merchantId, financingProgramId),
    enabled: !!merchantId && !!financingProgramId,
  })
  return [data ?? [], isFetching]
}

const getMerchantProviderProducts = async (merchantId: string, financingProgramId: number, providerId: string) => {
  const response = await getApiClient().get<MechanicalWarrantyProduct[]>(
    `/Merchants/${merchantId}/${financingProgramId}/Providers/${providerId}/Products`,
  )
  return response.data
}

export function useGetMerchantProviderProducts(
  merchantId: string,
  financingProgramId: number,
  providerId: string,
): [MechanicalWarrantyProduct[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.merchantProviderProducts(merchantId, financingProgramId, providerId),
    queryFn: () => getMerchantProviderProducts(merchantId, financingProgramId, providerId),
    enabled: !!merchantId && !!financingProgramId && !!providerId,
  })

  return [data ?? [], isFetching]
}
